/**
 * Side-Nav Shortcuts: Pins + Auto aus Nutzungszählern (localStorage).
 */

export const PLATFORM_SIDE_NAV_SHORTCUTS_PINNED_KEY =
  "tm.platform.sideNav.shortcuts.pinned";
export const PLATFORM_SIDE_NAV_SHORTCUTS_USAGE_KEY =
  "tm.platform.sideNav.shortcuts.usage";
export const PLATFORM_SIDE_NAV_SHORTCUTS_DISMISSED_AUTO_KEY =
  "tm.platform.sideNav.shortcuts.dismissedAuto";

export const PLATFORM_SIDE_NAV_SHORTCUTS_MAX_TOTAL = 6;
export const PLATFORM_SIDE_NAV_SHORTCUTS_MAX_AUTO = 3;

function __asId(v) {
  return String(v ?? "").trim();
}

function __readJson(key, fallback) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return fallback;
    const raw = window.localStorage.getItem(key);
    if (raw == null || raw === "") return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function __writeJson(key, value) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

/**
 * @returns {string[]}
 */
export function readSideNavPinnedShortcutIds() {
  const rows = __readJson(PLATFORM_SIDE_NAV_SHORTCUTS_PINNED_KEY, []);
  if (!Array.isArray(rows)) return [];
  return rows.map(__asId).filter(Boolean);
}

/**
 * @param {string[]} ids
 */
export function writeSideNavPinnedShortcutIds(ids) {
  const next = (Array.isArray(ids) ? ids : []).map(__asId).filter(Boolean);
  __writeJson(PLATFORM_SIDE_NAV_SHORTCUTS_PINNED_KEY, next);
  return next;
}

/**
 * @returns {Record<string, number>}
 */
export function readSideNavShortcutUsage() {
  const obj = __readJson(PLATFORM_SIDE_NAV_SHORTCUTS_USAGE_KEY, {});
  if (!obj || typeof obj !== "object") return {};
  /** @type {Record<string, number>} */
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const id = __asId(k);
    if (!id) continue;
    const n = Number(v) || 0;
    if (n > 0) out[id] = n;
  }
  return out;
}

/**
 * @param {Record<string, number>} usage
 */
export function writeSideNavShortcutUsage(usage) {
  const src = usage && typeof usage === "object" ? usage : {};
  /** @type {Record<string, number>} */
  const next = {};
  for (const [k, v] of Object.entries(src)) {
    const id = __asId(k);
    if (!id) continue;
    const n = Number(v) || 0;
    if (n > 0) next[id] = n;
  }
  __writeJson(PLATFORM_SIDE_NAV_SHORTCUTS_USAGE_KEY, next);
  return next;
}

/**
 * @param {string} id
 * @param {Record<string, number>} [usage]
 * @returns {Record<string, number>}
 */
export function bumpSideNavUsage(id, usage) {
  const key = __asId(id);
  if (!key) return usage && typeof usage === "object" ? { ...usage } : {};
  const base =
    usage && typeof usage === "object"
      ? { ...usage }
      : readSideNavShortcutUsage();
  base[key] = (Number(base[key]) || 0) + 1;
  return writeSideNavShortcutUsage(base);
}

/**
 * @returns {string[]}
 */
export function readSideNavDismissedAutoIds() {
  const rows = __readJson(PLATFORM_SIDE_NAV_SHORTCUTS_DISMISSED_AUTO_KEY, []);
  if (!Array.isArray(rows)) return [];
  return rows.map(__asId).filter(Boolean);
}

/**
 * @param {string[]} ids
 */
export function writeSideNavDismissedAutoIds(ids) {
  const next = (Array.isArray(ids) ? ids : []).map(__asId).filter(Boolean);
  __writeJson(PLATFORM_SIDE_NAV_SHORTCUTS_DISMISSED_AUTO_KEY, next);
  return next;
}

/**
 * Flache Leaf-Kandidaten aus sichtbaren Gruppen.
 * @param {import("./platformSideNavConfig.js").PlatformSideNavGroup[]} groups
 * @param {(key: string) => string} t
 * @returns {Array<{ id: string, href: string, label: string, groupLabel: string, appKey?: string|null }>}
 */
export function collectSideNavShortcutCandidates(groups, t) {
  const translate = typeof t === "function" ? t : (k) => k;
  /** @type {Array<{ id: string, href: string, label: string, groupLabel: string, appKey?: string|null }>} */
  const out = [];
  for (const group of Array.isArray(groups) ? groups : []) {
    const groupLabel = String(
      translate(group.labelKey) || group.labelKey || "",
    ).trim();
    const children = Array.isArray(group.children) ? group.children : [];
    if (!children.length) {
      const href = String(group.href || "").trim();
      if (!href) continue;
      out.push({
        id: __asId(group.id),
        href,
        label: groupLabel,
        groupLabel: "",
        appKey: group.appKey ?? null,
      });
      continue;
    }
    for (const child of children) {
      if (String(child?.kind || "") === "section") continue;
      const href = String(child.href || "").trim();
      const id = __asId(child.id);
      if (!href || !id) continue;
      out.push({
        id,
        href,
        label: String(translate(child.labelKey) || child.labelKey || "").trim(),
        groupLabel,
        appKey: child.appKey ?? null,
      });
    }
  }
  return out;
}

/**
 * @param {{
 *   groups: import("./platformSideNavConfig.js").PlatformSideNavGroup[],
 *   t: (key: string) => string,
 *   pinned?: string[],
 *   usage?: Record<string, number>,
 *   dismissed?: string[],
 *   maxTotal?: number,
 *   maxAuto?: number,
 * }} opts
 * @returns {Array<{
 *   id: string,
 *   href: string,
 *   label: string,
 *   groupLabel: string,
 *   appKey?: string|null,
 *   source: "pin"|"auto",
 * }>}
 */
export function resolveSideNavShortcutRows(opts = {}) {
  const t = typeof opts.t === "function" ? opts.t : (k) => k;
  const candidates = collectSideNavShortcutCandidates(opts.groups, t);
  const byId = new Map(candidates.map((c) => [c.id, c]));

  const pinnedRaw = Array.isArray(opts.pinned) ? opts.pinned : [];
  const dismissed = new Set(
    (Array.isArray(opts.dismissed) ? opts.dismissed : [])
      .map(__asId)
      .filter(Boolean),
  );
  const usage = opts.usage && typeof opts.usage === "object" ? opts.usage : {};
  const maxTotal = Math.max(
    1,
    Number(opts.maxTotal) || PLATFORM_SIDE_NAV_SHORTCUTS_MAX_TOTAL,
  );
  const maxAuto = Math.max(
    0,
    Number(opts.maxAuto) || PLATFORM_SIDE_NAV_SHORTCUTS_MAX_AUTO,
  );

  /** @type {Array<{ id: string, href: string, label: string, groupLabel: string, appKey?: string|null, source: "pin"|"auto" }>} */
  const rows = [];
  const used = new Set();

  for (const rawId of pinnedRaw) {
    if (rows.length >= maxTotal) break;
    const id = __asId(rawId);
    if (!id || used.has(id)) continue;
    const hit = byId.get(id);
    if (!hit) continue;
    used.add(id);
    rows.push({ ...hit, source: "pin" });
  }

  const autoSlots = Math.min(maxAuto, Math.max(0, maxTotal - rows.length));
  if (autoSlots > 0) {
    const ranked = candidates
      .filter((c) => !used.has(c.id) && !dismissed.has(c.id))
      .map((c) => ({
        c,
        score: Number(usage[c.id]) || 0,
      }))
      .filter((x) => x.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.c.label.localeCompare(b.c.label, "de");
      });

    for (const { c } of ranked) {
      if (rows.filter((r) => r.source === "auto").length >= autoSlots) break;
      if (rows.length >= maxTotal) break;
      if (used.has(c.id)) continue;
      used.add(c.id);
      rows.push({ ...c, source: "auto" });
    }
  }

  return rows;
}

/**
 * @param {string[]} pinned
 * @param {string} id
 * @param {number} [index]
 */
export function pinSideNavShortcutId(pinned, id, index) {
  const key = __asId(id);
  if (!key) return Array.isArray(pinned) ? [...pinned] : [];
  const cur = (Array.isArray(pinned) ? pinned : []).map(__asId).filter(Boolean);
  const without = cur.filter((x) => x !== key);
  if (index == null || !Number.isFinite(index)) {
    return [...without, key];
  }
  const i = Math.max(0, Math.min(without.length, Math.trunc(index)));
  const next = [...without];
  next.splice(i, 0, key);
  return next;
}

/**
 * @param {string[]} pinned
 * @param {string} id
 */
export function unpinSideNavShortcutId(pinned, id) {
  const key = __asId(id);
  return (Array.isArray(pinned) ? pinned : [])
    .map(__asId)
    .filter((x) => x && x !== key);
}

/**
 * @param {string[]} pinned
 * @param {string} fromId
 * @param {string} toId
 */
export function reorderSideNavPinnedShortcutIds(pinned, fromId, toId) {
  const from = __asId(fromId);
  const to = __asId(toId);
  const cur = (Array.isArray(pinned) ? pinned : []).map(__asId).filter(Boolean);
  if (!from || !to || from === to) return cur;
  const fromIdx = cur.indexOf(from);
  const toIdx = cur.indexOf(to);
  if (fromIdx < 0 || toIdx < 0) return cur;
  const next = [...cur];
  next.splice(fromIdx, 1);
  next.splice(toIdx, 0, from);
  return next;
}

/**
 * @param {string[]} dismissed
 * @param {string} id
 */
export function dismissSideNavAutoShortcutId(dismissed, id) {
  const key = __asId(id);
  if (!key) return Array.isArray(dismissed) ? [...dismissed] : [];
  const cur = (Array.isArray(dismissed) ? dismissed : [])
    .map(__asId)
    .filter(Boolean);
  if (cur.includes(key)) return cur;
  return [...cur, key];
}
